import type { Project, ProjectIndex, ProjectSummary } from '../domain/project.js';
import type { Assignment } from '../domain/assignment.js';
import type { Dependency } from '../domain/dependency.js';
import type { Task } from '../domain/task.js';
import { EntityNotFoundError, InvalidArgumentError, InvalidHierarchyError } from '../domain/errors.js';
import { buildScheduleGraph } from './scheduling-graph.js';
import { validateBudgetAssignments } from './budget.js';

function appendToIndex<T>(index: Map<string, T[]>, key: string, value: T): void {
  const bucket = index.get(key);
  if (bucket === undefined) index.set(key, [value]);
  else bucket.push(value);
}

function collectById<T extends { readonly id: string }>(
  entities: readonly T[],
  entityName: string,
): Map<string, T> {
  const result = new Map<string, T>();
  for (const entity of entities) {
    const id = entity.id as string;
    if (result.has(id)) throw new InvalidArgumentError(`duplicate ${entityName} id: ${id}`);
    result.set(id, entity);
  }
  return result;
}

/** Verifies that the stored task array is a pre-order outline traversal. */
function assertOutlineOrder(tasks: readonly Task[]): void {
  const ancestors: Array<Task | undefined> = [];
  for (const task of tasks) {
    const level = task.outlineLevel;
    if (!Number.isInteger(level) || level < 1) {
      throw new InvalidHierarchyError(task.id, `outlineLevel must be a positive integer; got ${String(level)}`);
    }
    if (level === 1) {
      if (task.parentId !== null) {
        throw new InvalidHierarchyError(task.id, 'root task (outlineLevel 1) must not have a parent');
      }
    } else {
      const expectedParent = ancestors[level - 2];
      if (expectedParent === undefined) {
        throw new InvalidHierarchyError(task.id, `outlineLevel ${String(level)} has no preceding level ${String(level - 1)} parent`);
      }
      if (task.parentId === null || (task.parentId as string) !== (expectedParent.id as string)) {
        throw new InvalidHierarchyError(
          task.id,
          `parent must be the nearest preceding level ${String(level - 1)} task (${expectedParent.id as string})`,
        );
      }
    }
    ancestors.length = level;
    ancestors[level - 1] = task;
  }
}

/**
 * 建立以 id 為 key 的查詢索引，供各引擎共用。
 * @throws {InvalidHierarchyError} 大綱結構不合法時。
 * @throws {InvalidDependencyError} 相依關係指向不存在的任務時。
 */
export function buildProjectIndex(project: Project): ProjectIndex {
  validateBudgetAssignments(project);
  assertOutlineOrder(project.tasks);
  // Reuse the scheduling graph as the single validation authority: this includes
  // hierarchy edges and FS/SS summary-dependency propagation.
  const scheduleGraph = buildScheduleGraph(project);
  const tasksById = scheduleGraph.tasksById;
  const resourcesById = collectById(project.resources, 'resource');
  const calendarsById = collectById(project.calendars, 'calendar');
  const childrenByParentId = scheduleGraph.childrenByParent;

  const assignmentsByTaskId = new Map<string, Assignment[]>();
  const assignmentsByResourceId = new Map<string, Assignment[]>();
  for (const assignment of project.assignments) {
    if (!tasksById.has(assignment.taskId as string)) {
      throw new EntityNotFoundError('TASK_NOT_FOUND', assignment.taskId as string);
    }
    if (!resourcesById.has(assignment.resourceId as string)) {
      throw new EntityNotFoundError('RESOURCE_NOT_FOUND', assignment.resourceId as string);
    }
    if (resourcesById.get(assignment.resourceId as string)?.isBudget) {
      throw new InvalidArgumentError(`budget resource ${assignment.resourceId as string} must use budgetAssignments, not assignments`);
    }
    appendToIndex(assignmentsByTaskId, assignment.taskId as string, assignment);
    appendToIndex(assignmentsByResourceId, assignment.resourceId as string, assignment);
  }

  const successorsByTaskId = new Map<string, Dependency[]>();
  const predecessorsByTaskId = new Map<string, Dependency[]>();
  for (const dependency of project.dependencies) {
    appendToIndex(successorsByTaskId, dependency.predecessorId as string, dependency);
    appendToIndex(predecessorsByTaskId, dependency.successorId as string, dependency);
  }

  const outlineOrder: string[] = [];
  const stack = project.tasks.filter((task) => task.parentId === null).reverse();
  while (stack.length > 0) {
    const task = stack.pop();
    if (task === undefined) continue;
    outlineOrder.push(task.id as string);
    const children = childrenByParentId.get(task.id as string);
    if (children !== undefined) {
      for (let i = children.length - 1; i >= 0; i -= 1) stack.push(children[i]!);
    }
  }

  return {
    tasksById,
    childrenByParentId,
    resourcesById,
    calendarsById,
    assignmentsByTaskId,
    assignmentsByResourceId,
    successorsByTaskId,
    predecessorsByTaskId,
    outlineOrder,
  };
}

/**
 * 計算專案層級彙總統計。
 *
 * taskCount 與 milestoneCount 計數所有實體任務；totalWork 與 totalDuration
 * 只加總沒有子任務的葉節點，避免摘要任務的彙總值被重複計入。start/finish
 * 一律使用專案目前排程錨點，故空專案的結果也是確定的。
 */
export function summarizeProject(project: Project): ProjectSummary {
  const index = buildProjectIndex(project);
  let milestoneCount = 0;
  let totalWork = 0;
  let totalDuration = 0;
  for (const task of project.tasks) {
    if (task.isMilestone) milestoneCount += 1;
    if ((index.childrenByParentId.get(task.id as string)?.length ?? 0) === 0) {
      totalWork += task.work as number;
      totalDuration += task.durationMinutes as number;
    }
  }
  return {
    taskCount: project.tasks.length,
    milestoneCount,
    totalWork: totalWork as ProjectSummary['totalWork'],
    totalDuration: totalDuration as ProjectSummary['totalDuration'],
    start: project.startDate,
    finish: project.finishDate,
  };
}
